import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAllLampComponents } from "../services/productService";
import LampViewer from "../components/LampViewer";
import { DEFAULT_BASE_COLOR_HEX, DEFAULT_SHADE_COLOR_HEX, DEFAULT_BASE_COLOR_ID, DEFAULT_SHADE_COLOR_ID, PRODUCT_NAME_PREFIX } from "../constants/defaults";
import { formatPrice, extractNumber, generateModelCode } from "../utils/formatters";
import { getColorDisplayName } from "../utils/colorNames";
import { PENDING_CART_ITEM_KEY } from "../constants/storageKeys";
import "./Configurator.css";

/**
 * Configurator Component
 *
 * Fetches components from API (only active ones with isActive=true)
 * When admin soft-deletes a component, it won't appear here
 */
export default function Configurator() {
  const [searchParams] = useSearchParams();

  // Component data from API
  const [bases, setBases] = useState([]);
  const [shades, setShades] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selected items
  const [selectedBaseId, setSelectedBaseId] = useState(null);
  const [selectedBaseColor, setSelectedBaseColor] = useState(null);
  const [selectedShadeId, setSelectedShadeId] = useState(null);
  const [selectedShadeColor, setSelectedShadeColor] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lampViewerRef = useRef();

  // Fetch components from API on mount
  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all active components from API
        const { bases: basesData, shades: shadesData, colors: colorsData } =
          await getAllLampComponents();

        // Transform API response to component format
        const transformedBases = basesData.map((b) => ({
          id: b.id,
          code: b.productComponentCode,
          name: b.productComponentName,
          preview: b.attributes?.thumbnailUrl || `/images/previews/bases/base_${b.displayOrder}_preview.png`,
          modelPath: b.attributes?.modelPath,
          displayOrder: b.displayOrder,
          price: parseFloat(b.price) || 0,
        }));

        const transformedShades = shadesData.map((s) => ({
          id: s.id,
          code: s.productComponentCode,
          name: s.productComponentName,
          preview: s.attributes?.thumbnailUrl || `/images/previews/shades/shade_${s.displayOrder}_preview.png`,
          modelPath: s.attributes?.modelPath,
          displayOrder: s.displayOrder,
          price: parseFloat(s.price) || 0,
        }));

        const transformedColors = colorsData.map((c) => ({
          id: c.productComponentCode, // Use code as ID for colors (white, black, etc.)
          label: c.productComponentName,
          swatch: c.attributes?.hexCode || '#ffffff',
          price: parseFloat(c.price) || 0, // Premium price for colors
        }));

        setBases(transformedBases);
        setShades(transformedShades);
        setColors(transformedColors);

        // Set default selections (first item of each)
        if (transformedBases.length > 0) {
          setSelectedBaseId(transformedBases[0].id);
        }
        if (transformedShades.length > 0) {
          setSelectedShadeId(transformedShades[0].id);
        }
        if (transformedColors.length > 0) {
          // Default: black for base, ivory for shade
          const blackColor = transformedColors.find(c => c.id === DEFAULT_BASE_COLOR_ID);
          const ivoryColor = transformedColors.find(c => c.id === DEFAULT_SHADE_COLOR_ID);
          setSelectedBaseColor(blackColor?.id || transformedColors[0].id);
          setSelectedShadeColor(ivoryColor?.id || transformedColors[0].id);
        }

      } catch (err) {
        console.error('Error fetching components:', err);
        setError('Failed to load lamp components. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  // Initialize from URL parameters if provided (after components load)
  useEffect(() => {
    if (loading || bases.length === 0) return;

    const baseId = searchParams.get('base');
    const shadeId = searchParams.get('shade');
    const baseColor = searchParams.get('baseColor');
    const shadeColor = searchParams.get('shadeColor');

    // Find by code, ID, or displayOrder
    if (baseId) {
      const base = bases.find(b => b.id === baseId || b.code === baseId || String(b.displayOrder) === baseId);
      if (base) setSelectedBaseId(base.id);
    }
    if (shadeId) {
      const shade = shades.find(s => s.id === shadeId || s.code === shadeId || String(s.displayOrder) === shadeId);
      if (shade) setSelectedShadeId(shade.id);
    }
    if (baseColor) {
      const color = colors.find(c => c.id === baseColor);
      if (color) setSelectedBaseColor(color.id);
    }
    if (shadeColor) {
      const color = colors.find(c => c.id === shadeColor);
      if (color) setSelectedShadeColor(color.id);
    }
  }, [searchParams, loading, bases, shades, colors]);

  // Current selections
  const currentBase = bases.find((b) => b.id === selectedBaseId);
  const currentShade = shades.find((s) => s.id === selectedShadeId);
  const currentBaseColor = colors.find((c) => c.id === selectedBaseColor);
  const currentShadeColor = colors.find((c) => c.id === selectedShadeColor);

  const baseColorHex = currentBaseColor?.swatch || DEFAULT_BASE_COLOR_HEX;
  const shadeColorHex = currentShadeColor?.swatch || DEFAULT_SHADE_COLOR_HEX;

  // Calculate dynamic price
  const basePrice = currentBase?.price || 0;
  const shadePrice = currentShade?.price || 0;
  const baseColorPremium = currentBaseColor?.price || 0;
  const shadeColorPremium = currentShadeColor?.price || 0;
  const totalPrice = basePrice + shadePrice + baseColorPremium + shadeColorPremium;

  // Generate model code and full name
  const modelCode = generateModelCode(currentBase?.code, currentShade?.code);
  const modelName = `${PRODUCT_NAME_PREFIX} ${currentBase?.name || 'Base'} \u00d7 ${currentShade?.name || 'Shade'}`;

  // Get base number for 3D viewer (extract from code like "B01" -> 1)
  const baseNumber = extractNumber(currentBase?.code);
  const shadeNumber = extractNumber(currentShade?.code);

  const handleAddToCart = () => {
    if (!currentBase || !currentShade) {
      alert('Please select a base and shade');
      return;
    }

    // Capture 3D canvas as image for cart display
    let snapshotImage = null;
    if (lampViewerRef.current) {
      snapshotImage = lampViewerRef.current.captureImage();
    }

    const cartItem = {
      baseId: currentBase.id,
      shadeId: currentShade.id,
      baseCode: currentBase.code,
      shadeCode: currentShade.code,
      baseColor: selectedBaseColor,
      shadeColor: selectedShadeColor,
      baseColorHex: baseColorHex,
      shadeColorHex: shadeColorHex,
      baseName: currentBase?.name,
      shadeName: currentShade?.name,
      baseColorName: getColorDisplayName(currentBaseColor?.id) || currentBaseColor?.label || 'Soft White',
      shadeColorName: getColorDisplayName(currentShadeColor?.id) || currentShadeColor?.label || 'Porcelain Ivory',
      modelCode,
      modelName,
      price: totalPrice,
      previewImage: snapshotImage || `/images/previews/bases/base_${baseNumber}_preview.png`,
    };

    console.log('Configurator: Adding to cart', cartItem);

    if (!isAuthenticated) {
      sessionStorage.setItem(PENDING_CART_ITEM_KEY, JSON.stringify(cartItem));
      console.log('Configurator: User not authenticated, redirecting to login');
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
      return;
    }

    addToCart(cartItem);
    console.log('Configurator: Item added to cart');

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Loading state
  if (loading) {
    return (
      <div className="configurator-page">
        <div className="configurator-loading">
          <div className="loading-spinner"></div>
          <p>Loading lamp components...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="configurator-page">
        <div className="configurator-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  // No components available
  if (bases.length === 0 || shades.length === 0 || colors.length === 0) {
    return (
      <div className="configurator-page">
        <div className="configurator-error">
          <p>No lamp components available. Please contact support.</p>
        </div>
      </div>
    );
  }

  // Find selected index for display
  const selectedBaseIndex = bases.findIndex(b => b.id === selectedBaseId) + 1;
  const selectedShadeIndex = shades.findIndex(s => s.id === selectedShadeId) + 1;

  return (
    <div className="configurator-page">
      <motion.div
        className="configurator-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Left Side Panel */}
        <section className="config-panel">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            Shade
          </motion.h2>

          {/* Shade Pattern Thumbnails */}
          <div className="control-group">
            <label className="control-label">
              Pattern
              <span className="control-badge">
                {selectedShadeIndex} of {shades.length}
              </span>
            </label>
            <div className="preview-grid-container">
              <div className="preview-grid">
                {shades.map((shade, index) => (
                  <motion.div
                    key={shade.id}
                    className={
                      "preview-item" +
                      (shade.id === selectedShadeId ? " preview-item--active" : "")
                    }
                    onClick={() => setSelectedShadeId(shade.id)}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.02 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img src={shade.preview} alt={shade.name} className="preview-img" />
                    <div className="preview-label">{shade.name}</div>
                  </motion.div>
                ))}
              </div>
              <div className="scroll-indicator">Scroll for more ↓</div>
            </div>
          </div>

          {/* Shade Colors */}
          <div className="control-group">
            <label className="control-label">
              Color
              <span className="control-subtext">
                &nbsp;({getColorDisplayName(currentShadeColor?.id) || currentShadeColor?.label})
              </span>
            </label>
            <div className="swatch-row">
              {colors.map((color) => (
                <motion.button
                  key={color.id + "_shade"}
                  className={
                    "swatch" +
                    (color.id === selectedShadeColor ? " swatch--active" : "")
                  }
                  style={{ backgroundColor: color.swatch }}
                  onClick={() => setSelectedShadeColor(color.id)}
                  aria-label={color.label}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>
          </div>

          <hr className="divider" />

          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            Base
          </motion.h2>

          {/* Base Pattern Thumbnails */}
          <div className="control-group">
            <label className="control-label">
              Pattern
              <span className="control-badge">
                {selectedBaseIndex} of {bases.length}
              </span>
            </label>
            <div className="preview-grid-container">
              <div className="preview-grid">
                {bases.map((base, index) => (
                  <motion.div
                    key={base.id}
                    className={
                      "preview-item" +
                      (base.id === selectedBaseId ? " preview-item--active" : "")
                    }
                    onClick={() => setSelectedBaseId(base.id)}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.02 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img src={base.preview} alt={base.name} className="preview-img" />
                    <div className="preview-label">{base.name}</div>
                  </motion.div>
                ))}
              </div>
              <div className="scroll-indicator">Scroll for more ↓</div>
            </div>
          </div>

          {/* Base Colors */}
          <div className="control-group">
            <label className="control-label">
              Color
              <span className="control-subtext">
                &nbsp;({getColorDisplayName(currentBaseColor?.id) || currentBaseColor?.label})
              </span>
            </label>
            <div className="swatch-row">
              {colors.map((color) => (
                <motion.button
                  key={color.id + "_base"}
                  className={
                    "swatch" +
                    (color.id === selectedBaseColor ? " swatch--active" : "")
                  }
                  style={{ backgroundColor: color.swatch }}
                  onClick={() => setSelectedBaseColor(color.id)}
                  aria-label={color.label}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>
          </div>

        </section>

        {/* Right Preview Panel */}
        <section className="preview-panel">
          <motion.div
            className="preview-card"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="preview-header">
              <div className="preview-header-left">
                <div className="preview-title">Live preview</div>
                <div className="preview-model-name">
                  {modelName} in {currentBaseColor?.label}
                </div>
                <div className="preview-meta">
                  <span className="model-code">{modelCode}</span>
                  {currentBase?.name} base · {currentShade?.name} shade
                </div>
              </div>
              <div className="preview-price-section">
                {totalPrice > 0 ? (
                  <div className="price-breakdown">
                    <div className="price-row">
                      <span>{currentBase?.name} Base</span>
                      <span>{formatPrice(basePrice)}</span>
                    </div>
                    <div className="price-row">
                      <span>{currentShade?.name} Shade</span>
                      <span>{formatPrice(shadePrice)}</span>
                    </div>
                    {baseColorPremium > 0 && (
                      <div className="price-row premium">
                        <span>{currentBaseColor?.label} (Base)</span>
                        <span>+{formatPrice(baseColorPremium)}</span>
                      </div>
                    )}
                    {shadeColorPremium > 0 && (
                      <div className="price-row premium">
                        <span>{currentShadeColor?.label} (Shade)</span>
                        <span>+{formatPrice(shadeColorPremium)}</span>
                      </div>
                    )}
                    <div className="price-row total">
                      <span>Total</span>
                      <span>{formatPrice(totalPrice)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="price-not-set">
                    <span>Price on request</span>
                  </div>
                )}
              </div>
              <motion.button
                className="add-to-cart-button-top"
                onClick={handleAddToCart}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                Add to Cart
              </motion.button>
            </div>

            {/* 3D VIEWER - Pass base/shade numbers for model loading */}
            <div className="preview-viewport">
              <LampViewer
                ref={lampViewerRef}
                baseId={String(baseNumber)}
                shadeId={String(shadeNumber)}
                baseColor={baseColorHex}
                shadeColor={shadeColorHex}
              />
            </div>
          </motion.div>
        </section>
      </motion.div>

      {/* Success Toast */}
      {showSuccess && (
        <motion.div
          className="success-toast"
          initial={{ opacity: 0, y: 50, x: "-50%" }}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: 50, x: "-50%" }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Added to cart successfully!
        </motion.div>
      )}
    </div>
  );
}
